import React from "react";
import TextField from '../Form/TextField';
import useTheme from '../../hooks/useTheme';
import { useTranslation } from "react-i18next";

export default function DeliveryForm(props) {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const {
        formData,
        handleChange
    } = props;
    const theme = useTheme();
  const isDelivery = formData.orderType === 'delivery';
  const isDriveThrough = formData.orderType === 'drive_through';
  const isDining = formData.orderType === 'dining';
  
  return (
    <div className="flex flex-col">
      <h1 className="text-xl md:text-2xl poppins pb-3 md:pb-4 border-b border-gray-500 text-gray-700">
        {isDelivery ? t('delivery_form.edit_delivery') : isDriveThrough ? t('delivery_form.edit_drive_through') : isDining ? t('delivery_form.edit_dining') : t('delivery_form.edit_customer')}
      </h1>
      <form className="my-3 md:my-4" >
        <div className="flex flex-col space-y-3 md:space-y-3">
          {!isDriveThrough && (
            <TextField
              type="text"
              placeholder={t('delivery_form.name_placeholder')}
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          )}
          {isDining && (
            <TextField
              type="text"
              placeholder={t('delivery_form.table_placeholder')}
              name="tableNumber"
              value={formData.tableNumber || ''}
              onChange={handleChange}
            />
          )}
          {isDelivery && (
            <>
              <TextField
                type="text"
                placeholder={t('delivery_form.building_placeholder')}
                name="building"
                value={formData.building}
                onChange={handleChange}
                required
              />
              <TextField
                type="text"
                placeholder={t('delivery_form.area_placeholder')}
                name="area"
                value={formData.area}
                onChange={handleChange}
                required
              />
            </>
          )}
          <TextField
            type="text"
            placeholder={t('delivery_form.mobile_placeholder')}
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
          />
          
          {/* Payment Method Selection - Hidden for dining */}
          {!isDining && (
          <div className="relative w-full px-4 py-4 rounded-lg border border-gray-300 bg-white">
            <div className="flex flex-col space-y-2.5">
              <label className={`flex items-center space-x-3 ${isArabic ? 'space-x-reverse' : ''} cursor-pointer group`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash_on_delivery"
                  checked={formData.paymentMethod === 'cash_on_delivery'}
                  onChange={handleChange}
                  className="w-5 h-5 cursor-pointer focus:outline-none"
                  style={{ accentColor: theme.colors.primaryColor }}
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium">
                  💵 {t('delivery_form.cash')}
                </span>
              </label>
              
              <label className={`flex items-center space-x-3 ${isArabic ? 'space-x-reverse' : ''} cursor-pointer group`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="card"
                  checked={formData.paymentMethod === 'card'}
                  onChange={handleChange}
                  className="w-5 h-5 cursor-pointer focus:outline-none"
                  style={{ accentColor: theme.colors.primaryColor }}
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium">
                  💳 {t('delivery_form.card')}
                </span>
              </label>
              
              <label className={`flex items-center space-x-3 ${isArabic ? 'space-x-reverse' : ''} cursor-pointer group`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="upi"
                  checked={formData.paymentMethod === 'upi'}
                  onChange={handleChange}
                  className="w-5 h-5 cursor-pointer focus:outline-none"
                  style={{ accentColor: theme.colors.primaryColor }}
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900 font-medium">
                  📱 {t('delivery_form.upi')}
                </span>
              </label>
            </div>
          </div>
          )}
          
          {isDriveThrough && (
            <>
              <TextField
                type="text"
                placeholder={t('delivery_form.eta_placeholder')}
                name="estimatedTime"
                value={formData.estimatedTime}
                onChange={handleChange}
              />
              <TextField
                type="text"
                placeholder={t('delivery_form.vehicle_placeholder')}
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
              />
            </>
          )}
          <TextField
            type="text"
            placeholder={t('delivery_form.kitchen_notes')}
            name="kitchenNotes"
            value={formData.kitchenNotes}
            onChange={handleChange}
          />
          
          {/* Use Current Location Checkbox - Only show for delivery orders */}
          {isDelivery && (
            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 hover:border-blue-300 transition-all duration-300">
              <div className={`flex items-center space-x-3 ${isArabic ? 'space-x-reverse' : ''}`}>
                <div className="relative">
                  <input
                    type="checkbox"
                    id="useCurrentLocation"
                    name="useCurrentLocation"
                    checked={formData.useCurrentLocation || false}
                    onChange={handleChange}
                    className="w-5 h-5 bg-white border-2 border-blue-300 rounded-md focus:ring-blue-500 focus:ring-2 focus:ring-offset-2 cursor-pointer transition-all duration-200"
                    style={{
                      backgroundImage: formData.useCurrentLocation ? 'url("data:image/svg+xml,%3csvg viewBox=\'0 0 16 16\' fill=\'white\' xmlns=\'http://www.w3.org/2000/svg\'%3e%3cpath d=\'m13.854 3.646-7.5 7.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6 10.293l7.146-7.147a.5.5 0 0 1 .708.708z\'/%3e%3c/svg%3e")' : 'none',
                      backgroundColor: formData.useCurrentLocation ? '#2563eb' : 'white',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none'
                    }}
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="useCurrentLocation" className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} cursor-pointer`}>
                    <span className="text-2xl">📍</span>
                    <div className={isArabic ? 'text-right' : 'text-left'}>
                      <div className="text-sm font-semibold text-gray-800">
                        {t('delivery_form.use_location')}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {t('delivery_form.share_location_desc')}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
